import React, { useState, useEffect } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
    Tag, Clock, TrendingUp, Filter, Loader2, ChevronDown, ChevronRight, Building2, UserCircle
} from 'lucide-react';
import api from '../services/api';

const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const RelatoriosCategoriasScreen = ({ currentUser }) => {
    const getLocalYYYYMMDD = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    const [startDate, setStartDate] = useState(getLocalYYYYMMDD(firstDay));
    const [endDate, setEndDate] = useState(getLocalYYYYMMDD(today));
    const [reportData, setReportData] = useState([]);
    const [grouped, setGrouped] = useState([]);
    const [byCompany, setByCompany] = useState([]);
    const [byAgent, setByAgent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('categoria');
    const [expandedRows, setExpandedRows] = useState({});

    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

    const fetchReportData = async () => {
        setLoading(true);
        try {
            const response = await api.get('/reports/categories', { params: { startDate, endDate } });
            setGrouped(response.data.metrics?.grouped || []);
            setReportData(response.data.chartData || []);
            setByCompany(response.data.byCompany || []);
            setByAgent(response.data.byAgent || []);
        } catch (error) {
            console.error("Erro ao gerar relatório:", error);
        } finally {
            setLoading(false);
            setExpandedRows({});
        }
    };

    useEffect(() => { fetchReportData(); }, [startDate, endDate]);

    const tabs = [
        { id: 'categoria', label: 'Categoria', icon: Tag },
        { id: 'empresa', label: 'Empresa', icon: Building2 },
        { id: 'atendente', label: 'Atendente', icon: UserCircle },
    ];

    return (
        <div className="p-6 h-full overflow-y-auto bg-gray-50">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <Tag className="mr-2 text-blue-600" /> Relatório por Categorias
                    </h2>
                    <p className="text-sm text-gray-500">Volume de atendimentos agrupado por categorias do sistema</p>
                </div>

                <div className="flex gap-2 items-center bg-white p-2 rounded-lg shadow-sm border border-gray-200">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="p-1.5 border rounded text-sm" />
                    <span className="text-gray-400">-</span>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="p-1.5 border rounded text-sm" />
                    <button onClick={fetchReportData} className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* GRÁFICO */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6">
                <h3 className="font-bold text-gray-700 mb-4">Top 10 Categorias (Volume)</h3>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={reportData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* TABS */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex border-b border-gray-200">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id); setExpandedRows({}); }}
                            className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-colors border-b-2 ${
                                activeTab === tab.id
                                    ? 'text-blue-600 border-blue-600 bg-blue-50/50'
                                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ═══ ABA: CATEGORIA ═══ */}
                {activeTab === 'categoria' && (
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 border-b uppercase text-xs text-gray-500">
                            <tr>
                                <th className="px-6 py-3">Categoria</th>
                                <th className="px-6 py-3 text-center">Total de Tíquetes</th>
                                <th className="px-6 py-3 text-center">Finalizados</th>
                                <th className="px-6 py-3 text-center">Taxa Resolução</th>
                                <th className="px-6 py-3 text-center">T.M.A.</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {grouped.length > 0 ? (
                                grouped.map((cat, idx) => (
                                    <React.Fragment key={idx}>
                                        <tr
                                            className={`hover:bg-gray-50 cursor-pointer transition-colors ${expandedRows[`cat-${idx}`] ? 'bg-blue-50/50' : ''}`}
                                            onClick={() => cat.subcategories.length > 0 && toggleRow(`cat-${idx}`)}
                                        >
                                            <td className="px-6 py-4 font-bold text-gray-700">
                                                <div className="flex items-center gap-2">
                                                    {cat.subcategories.length > 0 ? (
                                                        expandedRows[`cat-${idx}`]
                                                            ? <ChevronDown className="w-4 h-4 text-blue-500 shrink-0" />
                                                            : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                                                    ) : <Tag className="w-4 h-4 text-blue-400 shrink-0" />}
                                                    {cat.name}
                                                    {cat.subcategories.length > 0 && (
                                                        <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-semibold">
                                                            {cat.subcategories.length} sub
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center font-bold text-gray-800">{cat.total}</td>
                                            <td className="px-6 py-4 text-center text-green-600 font-medium">{cat.ended}</td>
                                            <td className="px-6 py-4 text-center font-bold text-blue-600">{cat.rate}%</td>
                                            <td className="px-6 py-4 text-center text-gray-500">{formatDuration(cat.tmaSeconds)}</td>
                                        </tr>
                                        {/* Subcategorias expandidas */}
                                        {expandedRows[`cat-${idx}`] && cat.subcategories.map((sub, sIdx) => (
                                            <tr key={`sub-${idx}-${sIdx}`} className="bg-gray-50/80 border-l-4 border-blue-300">
                                                <td className="px-6 py-3 pl-14 text-gray-600">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0"></span>
                                                        {sub.name}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 text-center font-semibold text-gray-700">{sub.total}</td>
                                                <td className="px-6 py-3 text-center text-green-600">{sub.ended}</td>
                                                <td className="px-6 py-3 text-center text-blue-600">{sub.rate}%</td>
                                                <td className="px-6 py-3 text-center text-gray-500">{formatDuration(sub.tmaSeconds)}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="text-center py-8 text-gray-400">Sem dados para o período.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}

                {/* ═══ ABA: EMPRESA ═══ */}
                {activeTab === 'empresa' && (
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 border-b uppercase text-xs text-gray-500">
                            <tr>
                                <th className="px-6 py-3">Empresa</th>
                                <th className="px-6 py-3 text-center">Total Categorizado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {byCompany.length > 0 ? (
                                byCompany.map((comp, idx) => (
                                    <React.Fragment key={idx}>
                                        <tr
                                            className={`hover:bg-gray-50 cursor-pointer transition-colors ${expandedRows[`comp-${idx}`] ? 'bg-green-50/50' : ''}`}
                                            onClick={() => toggleRow(`comp-${idx}`)}
                                        >
                                            <td className="px-6 py-4 font-bold text-gray-700">
                                                <div className="flex items-center gap-2">
                                                    {expandedRows[`comp-${idx}`]
                                                        ? <ChevronDown className="w-4 h-4 text-green-500 shrink-0" />
                                                        : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                                                    }
                                                    <Building2 className="w-4 h-4 text-green-500 shrink-0" />
                                                    {comp.name}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center font-bold text-gray-800">{comp.total}</td>
                                        </tr>
                                        {expandedRows[`comp-${idx}`] && comp.categories.map((cat, cIdx) => (
                                            <tr key={`comp-cat-${idx}-${cIdx}`} className="bg-gray-50/80 border-l-4 border-green-300">
                                                <td className="px-6 py-3 pl-14 text-gray-600">
                                                    <div className="flex items-center gap-2">
                                                        <Tag className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                                        {cat.name}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 text-center font-semibold text-gray-700">{cat.total}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr><td colSpan="2" className="text-center py-8 text-gray-400">Sem dados para o período.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}

                {/* ═══ ABA: ATENDENTE ═══ */}
                {activeTab === 'atendente' && (
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 border-b uppercase text-xs text-gray-500">
                            <tr>
                                <th className="px-6 py-3">Atendente</th>
                                <th className="px-6 py-3 text-center">Total Categorizado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {byAgent.length > 0 ? (
                                byAgent.map((agent, idx) => (
                                    <React.Fragment key={idx}>
                                        <tr
                                            className={`hover:bg-gray-50 cursor-pointer transition-colors ${expandedRows[`agent-${idx}`] ? 'bg-purple-50/50' : ''}`}
                                            onClick={() => toggleRow(`agent-${idx}`)}
                                        >
                                            <td className="px-6 py-4 font-bold text-gray-700">
                                                <div className="flex items-center gap-2">
                                                    {expandedRows[`agent-${idx}`]
                                                        ? <ChevronDown className="w-4 h-4 text-purple-500 shrink-0" />
                                                        : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                                                    }
                                                    <UserCircle className="w-4 h-4 text-purple-500 shrink-0" />
                                                    {agent.name}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center font-bold text-gray-800">{agent.total}</td>
                                        </tr>
                                        {expandedRows[`agent-${idx}`] && agent.categories.map((cat, cIdx) => (
                                            <tr key={`agent-cat-${idx}-${cIdx}`} className="bg-gray-50/80 border-l-4 border-purple-300">
                                                <td className="px-6 py-3 pl-14 text-gray-600">
                                                    <div className="flex items-center gap-2">
                                                        <Tag className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                                        {cat.name}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 text-center font-semibold text-gray-700">{cat.total}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr><td colSpan="2" className="text-center py-8 text-gray-400">Sem dados para o período.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default RelatoriosCategoriasScreen;
